import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { usePostHog } from 'posthog-js/react';
import { IconCheck } from '@tabler/icons-react';
import { validateEmailSyntax, suggestEmailCorrection } from '../utils/emailValidation';
import './AccountEmailRow.css';

/**
 * Settings row showing the account's sending address with an inline change
 * form. Deliberately mirrors EmailRegistrationCard's validate/suggest/confirm
 * machine instead of sharing it: the card is deployed and must stay untouched.
 */
const AccountEmailRow = ({ email, onChanged }) => {
    const { t } = useTranslation();
    const posthog = usePostHog();
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [suggestion, setSuggestion] = useState(null);
    const [pendingConfirmation, setPendingConfirmation] = useState(null);
    const [justSaved, setJustSaved] = useState(false);

    const startEditing = () => {
        setInputValue(email || '');
        setError('');
        setSuggestion(null);
        setPendingConfirmation(null);
        setJustSaved(false);
        setIsEditing(true);
    };

    const stopEditing = () => {
        setIsEditing(false);
        setInputValue('');
        setError('');
        setSuggestion(null);
        setPendingConfirmation(null);
    };

    // Typo hint, computed on blur so it doesn't flicker mid-typing.
    // Purely advisory: it never blocks submission.
    const handleBlur = () => {
        const next = suggestEmailCorrection(inputValue);
        setSuggestion(next);
        if (next) {
            posthog?.capture('email_suggestion_shown', {
                suggested_domain: next.suggestedDomain,
                component: 'AccountEmailRow'
            });
        }
    };

    const handleChange = (e) => {
        setInputValue(e.target.value);
        setSuggestion(null);
        setPendingConfirmation(null);
    };

    const applySuggestion = () => {
        if (!suggestion) return;
        setInputValue(suggestion.suggestion);
        setSuggestion(null);
        setError('');
        posthog?.capture('email_suggestion_accepted', {
            suggested_domain: suggestion.suggestedDomain,
            component: 'AccountEmailRow'
        });
    };

    const performChange = async (address) => {
        if (email && address === email) {
            stopEditing();
            return;
        }
        setIsSaving(true);
        try {
            const changeAccountEmailFunc = httpsCallable(functions, 'changeAccountEmail');
            const result = await changeAccountEmailFunc({ email: address });
            if (onChanged) {
                onChanged(result.data);
            }
            stopEditing();
            setJustSaved(true);
            setTimeout(() => setJustSaved(false), 3000);
        } catch (err) {
            console.error('Error changing account email:', err);
            // functions/invalid-argument is the server rejecting the address
            // itself (e.g. unknown TLD), not a transient failure.
            if (err.code === 'functions/invalid-argument') {
                setError(t('emailRegistration.errorInvalidEmail', 'Please enter a valid email address'));
            } else {
                setError(t('settingsPage.emailChangeError', 'Failed to update email. Please try again.'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const { valid, normalized } = validateEmailSyntax(inputValue);
        if (!valid) {
            setError(t('emailRegistration.errorInvalidEmail', 'Please enter a valid email address'));
            return;
        }

        // Recomputed at submit time: the blur hint can be skipped entirely by
        // an Enter-key submit. A suspected typo pauses the save until the user
        // explicitly picks an address; it still never hard-blocks.
        const next = suggestEmailCorrection(normalized);
        if (next) {
            setSuggestion(null);
            setPendingConfirmation({
                typed: normalized,
                suggestion: next.suggestion,
                suggestedDomain: next.suggestedDomain
            });
            posthog?.capture('email_typo_confirm_shown', {
                suggested_domain: next.suggestedDomain,
                component: 'AccountEmailRow'
            });
            return;
        }

        await performChange(normalized);
    };

    const acceptSuggestedEmail = async () => {
        if (!pendingConfirmation) return;
        const address = pendingConfirmation.suggestion;
        setInputValue(address);
        setPendingConfirmation(null);
        setSuggestion(null);
        posthog?.capture('email_typo_confirm_accepted', {
            suggested_domain: pendingConfirmation.suggestedDomain,
            component: 'AccountEmailRow'
        });
        await performChange(address);
    };

    const keepTypedEmail = async () => {
        if (!pendingConfirmation) return;
        const address = pendingConfirmation.typed;
        setPendingConfirmation(null);
        setSuggestion(null);
        posthog?.capture('email_typo_confirm_overridden', {
            suggested_domain: pendingConfirmation.suggestedDomain,
            component: 'AccountEmailRow'
        });
        await performChange(address);
    };

    return (
        <div className="preference-item account-email-row">
            <div className="preference-info account-email-info">
                <h4>{t('settingsPage.emailAddress', 'Email address')}</h4>
                {!isEditing && (
                    <p className="account-email-current">
                        {email || t('settingsPage.noEmailAddress', 'No email address on your account')}
                    </p>
                )}
                {justSaved && !isEditing && (
                    <div className="challenge-profile-saved account-email-saved">
                        <IconCheck size={16} />
                        {t('settingsPage.emailUpdated', 'Email updated')}
                    </div>
                )}
                {isEditing && (
                    <form className="account-email-form" onSubmit={handleSubmit}>
                        <input
                            type="email"
                            name="email"
                            autoComplete="email"
                            inputMode="email"
                            autoCapitalize="none"
                            spellCheck={false}
                            maxLength={254}
                            placeholder={t('settingsPage.enterEmailPlaceholder', 'you@example.com')}
                            value={inputValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isSaving}
                            className="challenge-name-input account-email-input"
                        />
                        <div className="account-email-actions">
                            <button
                                type="submit"
                                className="account-email-save"
                                disabled={isSaving || !inputValue.trim()}
                            >
                                {isSaving ? t('settingsPage.savingEmail', 'Saving...') : t('settingsPage.saveEmail', 'Save')}
                            </button>
                            <button
                                type="button"
                                className="account-email-cancel"
                                onClick={stopEditing}
                                disabled={isSaving}
                            >
                                {t('settingsPage.cancelEmail', 'Cancel')}
                            </button>
                        </div>
                        {error && (
                            <div className="account-email-error">{error}</div>
                        )}
                        {suggestion && !pendingConfirmation && (
                            <div className="account-email-suggestion">
                                <span>{t('emailRegistration.didYouMean', 'Did you mean')}</span>
                                <button
                                    type="button"
                                    className="account-email-suggestion-button"
                                    onClick={applySuggestion}
                                >
                                    {suggestion.suggestion}
                                </button>
                                <span>?</span>
                            </div>
                        )}
                        {pendingConfirmation && (
                            <div className="account-email-confirm">
                                <span>{t('emailRegistration.confirmTypoTitle', 'That address looks like it might have a typo.')}</span>
                                <div className="account-email-confirm-actions">
                                    <button
                                        type="button"
                                        className="account-email-confirm-button"
                                        onClick={acceptSuggestedEmail}
                                        disabled={isSaving}
                                    >
                                        {t('emailRegistration.useSuggested', `Use ${pendingConfirmation.suggestion}`, { email: pendingConfirmation.suggestion })}
                                    </button>
                                    <button
                                        type="button"
                                        className="account-email-confirm-button secondary"
                                        onClick={keepTypedEmail}
                                        disabled={isSaving}
                                    >
                                        {t('settingsPage.keepTypedEmail', `Use ${pendingConfirmation.typed} anyway`, { email: pendingConfirmation.typed })}
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>
                )}
            </div>
            {!isEditing && (
                <div className="preference-control">
                    <button type="button" className="account-email-change" onClick={startEditing}>
                        {email ? t('settingsPage.changeEmail', 'Change') : t('settingsPage.addEmail', 'Add')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default AccountEmailRow;
